import React from "react";
import { Button } from "@/components/ui/button";
import { Crown, Sparkles, ArrowUpRight, Lock, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { FeatureSlug } from "@/lib/feature-gates";
import { featureSlugToName } from "@/lib/feature-gates";

interface FeaturePaywallProps {
  featureSlug: FeatureSlug;
  description?: string;
  children: React.ReactNode;
  planName?: string | null;
  icon?: React.ReactNode;
  isLocked?: boolean;
  isLoading?: boolean;
  benefits?: string[];
  onUpgrade?: () => void;
  upgradePath?: string;
}

const defaultBenefits = [
  "Acesse todos os relatórios avançados",
  "Análise de dados históricos completa",
  "Integrações ilimitadas",
  "Suporte prioritário",
  "Insights com IA",
];

const FeaturePaywall: React.FC<FeaturePaywallProps> = ({
  featureSlug,
  description,
  children,
  planName,
  icon,
  isLocked = true,
  isLoading = false,
  benefits = defaultBenefits,
  onUpgrade,
  upgradePath,
}) => {
  const featureName = featureSlugToName[featureSlug];
  const navigate = useNavigate();

  const showPaywall = !isLoading && isLocked;

  const handleUpgrade =
    onUpgrade ?? (() => navigate(upgradePath ?? "/dashboard/my-account"));

  return (
    <div className={cn(showPaywall && "relative min-h-screen")}>
      <div
        className={cn(
          "transition-all duration-500 ease-in-out",
          showPaywall && "pointer-events-none select-none min-h-screen",
        )}
        aria-hidden={showPaywall}
      >
        {children}
      </div>

      <div
        className={cn(
          "bg-gradient-to-b from-background/5 via-background/70 to-background transition-opacity duration-500 ease-in-out",
          showPaywall
            ? "opacity-100 fixed inset-0"
            : "opacity-0 pointer-events-none",
        )}
      />

      <div
        className={cn(
          "flex items-center justify-center transition-all duration-500 ease-in-out",
          showPaywall
            ? "opacity-100 fixed inset-0"
            : "opacity-0 pointer-events-none",
        )}
      >
        <div
          className={cn(
            "max-w-md w-full mx-4 transition-all duration-500 ease-out",
            showPaywall
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-0",
          )}
        >
          <div className="relative rounded-2xl border border-border/50 bg-card/95 backdrop-blur-xl p-8 shadow-2xl text-center space-y-6">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-10 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative mx-auto w-16 h-16">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl opacity-20 blur-xl" />
              <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 border border-amber-300/30 flex items-center justify-center shadow-lg shadow-amber-500/20">
                {icon ?? <Crown className="h-7 w-7 text-white" />}
              </div>
            </div>

            <div className="relative space-y-2">
              <h3 className="text-xl font-bold text-foreground">
                {featureName}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {description ||
                  `Este recurso não está disponível no seu plano atual. Desbloqueie ${featureName.toLowerCase()} e muito mais com um plano superior.`}
              </p>
              {planName && (
                <div className="flex items-center justify-center gap-1.5">
                  <Lock className="h-3 w-3 text-muted-foreground/60" />
                  <span className="text-xs text-muted-foreground/60">
                    Disponível em planos superiores ao {planName}
                  </span>
                </div>
              )}
            </div>

            <div className="relative space-y-2.5 text-left bg-muted/30 rounded-xl p-4 border border-border/40">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Star className="h-3 w-3" />
                Ao fazer upgrade você ganha:
              </p>
              <ul className="space-y-2">
                {benefits.map((benefit, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-2.5 text-sm text-foreground/80"
                  >
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <Button
              size="lg"
              className="relative w-full gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300"
              onClick={handleUpgrade}
            >
              <Sparkles className="h-4 w-4" />
              Ver Planos e Preços
              <ArrowUpRight className="h-4 w-4" />
            </Button>

            <p className="relative text-xs text-muted-foreground/50">
              Desbloqueie todo o potencial da sua plataforma
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturePaywall;
